import { useTranslation } from 'react-i18next';

import { Icon } from '@sternum/web/components';
import { TextOverflowBox } from '@sternum/web/components/TextOverflowBox';
import { ThemeMode, ThemeProvider } from '@sternum/web/components/theme/main';
import { routes } from '@sternum/web/config/routes';
import { useGeneratePathWithCurrentClient } from '@sternum/web/features/auth/hooks';
import { useDefaultDashboard, useLastUsedDashboard } from '@sternum/web/features/dashboard/hooks';
import { namespaces } from '@sternum/web/services/i18n';

import { useMenu } from '../../hooks';
import { MenuItemExpandableLink } from '../MenuItemExpandableLink';
import { MenuItemExpandableLinkProps } from '../MenuItemExpandableLink/MenuItemExpandableLink.types';

import { LastUsedDashboardLink } from './MenuDashboardLink.styles';

export interface MenuDashboardLinkProps extends Pick<MenuItemExpandableLinkProps, 'variant' | 'icon'> {
  tooltipThemeMode?: ThemeMode;
}

export function MenuDashboardLink({ icon, variant = 'expanded', ...props }: MenuDashboardLinkProps) {
  const generatePath = useGeneratePathWithCurrentClient();
  const { t } = useTranslation(namespaces.appContainerWithMenu);
  const { showDashboardMenu } = useMenu();

  const { lastUsedDashboard, isFetching: isLoadingLastUsedDashboard } = useLastUsedDashboard();

  const { defaultDashboard, isFetching: isLoadingDefaultDashboard } = useDefaultDashboard({
    enabled: !isLoadingLastUsedDashboard && !lastUsedDashboard,
  });

  const onDashboardClick = () => {
    showDashboardMenu();
  };

  const getLastUsedDashboardName = () => {
    if (isLoadingLastUsedDashboard || isLoadingDefaultDashboard) {
      return <span aria-label="Loading title">{t('dashboardsLoading')}</span>;
    }

    return lastUsedDashboard?.title || defaultDashboard?.title || 'Error';
  };

  const dashboardId = lastUsedDashboard?.id || defaultDashboard?.id;

  return (
    <MenuItemExpandableLink
      to={dashboardId ? generatePath(routes.dashboard.fullDashboard.absolute, { dashboardId }) : '/'}
      title={t('menuItemDashboards')}
      variant={variant}
      icon={icon}
      {...props}
    >
      <ThemeProvider mode="Light">
        <LastUsedDashboardLink rightIcon={<Icon type="ChevronRightIcon" />} onClick={onDashboardClick}>
          <TextOverflowBox>{getLastUsedDashboardName()}</TextOverflowBox>
        </LastUsedDashboardLink>
      </ThemeProvider>
    </MenuItemExpandableLink>
  );
}
